import { StateStorage } from 'zustand/middleware';

/**
 * Enhanced persistence utilities for Zustand stores
 * Provides robust storage with error handling and migration support
 */

// Storage keys for different stores
export const STORAGE_KEYS = {
  AUTH: 'auth-store',
  CHAT: 'chat-store',
  ENRICHMENT: 'enrichment-store',
  TABLE: 'table-store',
  NAVIGATION: 'navigation-store',
  INFO_SIDEBAR: 'info-sidebar-store',
  RESTRICTIONS: 'restrictions-store',
} as const;

// Storage versions for migration support
export const STORAGE_VERSIONS = {
  [STORAGE_KEYS.AUTH]: 1,
  [STORAGE_KEYS.CHAT]: 1,
  [STORAGE_KEYS.ENRICHMENT]: 1,
  [STORAGE_KEYS.TABLE]: 1,
  [STORAGE_KEYS.NAVIGATION]: 1,
  [STORAGE_KEYS.INFO_SIDEBAR]: 1,
  [STORAGE_KEYS.RESTRICTIONS]: 1,
} as const;

/**
 * Enhanced localStorage with error handling and compression
 */
class EnhancedStorage implements StateStorage {
  private compress: boolean;
  private prefix: string;

  constructor(options: { compress?: boolean; prefix?: string } = {}) {
    this.compress = options.compress ?? false;
    this.prefix = options.prefix ?? 'disgenet-';
  }

  private getKey(name: string): string {
    return `${this.prefix}${name}`;
  }

  private compressData(data: string): string {
    if (!this.compress) return data;
    
    try {
      // Simple compression using JSON.stringify optimization
      const parsed = JSON.parse(data);
      return JSON.stringify(parsed);
    } catch {
      return data;
    }
  }

  private decompressData(data: string): string {
    // For now, just return the data as-is
    // In the future, we could implement actual compression
    return data;
  }

  getItem(name: string): string | null {
    try {
      const key = this.getKey(name);
      const item = localStorage.getItem(key);
      
      if (item === null) return null;
      
      const decompressed = this.decompressData(item);
      
      // Validate that it's valid JSON
      JSON.parse(decompressed);
      
      return decompressed;
    } catch (error) {
      console.warn(`Failed to get item from storage: ${name}`, error);
      return null;
    }
  }

  setItem(name: string, value: string): void {
    try {
      const key = this.getKey(name);
      const compressed = this.compressData(value);
      
      localStorage.setItem(key, compressed);
    } catch (error) {
      console.error(`Failed to set item in storage: ${name}`, error);
      
      // If storage is full, try to clear some space
      if (error instanceof DOMException && error.code === 22) {
        this.clearOldItems();
        
        // Try again
        try {
          localStorage.setItem(this.getKey(name), this.compressData(value));
        } catch (retryError) {
          console.error(`Failed to set item after cleanup: ${name}`, retryError);
        }
      }
    }
  }

  removeItem(name: string): void {
    try {
      const key = this.getKey(name);
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove item from storage: ${name}`, error);
    }
  }

  private clearOldItems(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.prefix)) {
          // Remove items that are older than 30 days
          const item = localStorage.getItem(key);
          if (item) {
            try {
              const parsed = JSON.parse(item);
              if (parsed.state && parsed.state.lastAccessed) {
                const lastAccessed = new Date(parsed.state.lastAccessed);
                const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                
                if (lastAccessed < thirtyDaysAgo) {
                  keysToRemove.push(key);
                }
              }
            } catch {
              // If we can't parse it, it's probably corrupted, so remove it
              keysToRemove.push(key);
            }
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Cleared old storage items
    } catch (error) {
      console.error('Failed to clear old storage items', error);
    }
  }
}

// Create storage instances
export const defaultStorage = new EnhancedStorage();
export const compressedStorage = new EnhancedStorage({ compress: true });

/**
 * Migration utilities for store state
 */
export interface StoreMigration<T = any> {
  version: number;
  migrate: (persistedState: any) => T;
}

export const createMigrationConfig = <T>(
  migrations: StoreMigration<T>[],
  currentVersion: number
) => ({
  migrate: (persistedState: any, version: number) => {
    let state = persistedState;
    
    // Apply migrations in sequence
    for (const migration of migrations) {
      if (migration.version > version && migration.version <= currentVersion) {
        try {
          state = migration.migrate(state);
        } catch (error) {
          console.error(`Migration failed for version ${migration.version}`, error);
          // Return null to reset the store if migration fails
          return null;
        }
      }
    }
    
    return state;
  },
  version: currentVersion,
});

/**
 * Partialize functions for selective persistence
 */

// Auth store - persist only essential auth state
export const partializeAuth = (state: any) => ({
  user: state.user,
  groups: state.groups,
  userType: state.userType,
  authorization: state.authorization,
  // Don't persist sensitive or temporary state
  // isAuthenticating: false,
  // authError: '',
});

// Chat store - persist chat history with size limit
export const partializeChat = (state: any) => ({
  chatHistory: state.chatHistory.slice(-50), // Keep only last 50 messages
});

// Table store - persist user preferences
export const partializeTable = (state: any) => ({
  columnVisibility: state.columnVisibility,
  columnOrder: state.columnOrder,
  pagination: {
    pageSize: state.pagination.pageSize, // Persist page size preference
    // Don't persist current page
  },
  // Don't persist temporary state like selections or modals
});

// Navigation store - don't persist navigation state (it should be URL-driven)
export const partializeNavigation = () => ({
  // Navigation state should be derived from URL, not persisted
});

// InfoSidebar store - persist user preferences
export const partializeInfoSidebar = (state: any) => ({
  width: state.width,
  position: state.position,
  // Don't persist open state or active tab
});

/**
 * Storage health check utilities
 */
export const checkStorageHealth = (): {
  isAvailable: boolean;
  usage: number;
  errors: string[];
} => {
  const errors: string[] = [];
  let isAvailable = false;
  let usage = 0;

  try {
    // Test localStorage availability
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    isAvailable = true;

    // Calculate storage usage
    let totalSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += key.length + value.length;
        }
      }
    }
    
    // Estimate usage as percentage (5MB typical limit)
    usage = (totalSize / (5 * 1024 * 1024)) * 100;
    
  } catch (error) {
    errors.push(`Storage not available: ${error}`);
  }

  return { isAvailable, usage, errors };
};

/**
 * Clear all store data (useful for logout or reset)
 */
export const clearAllStoreData = (): void => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      defaultStorage.removeItem(key);
    });
    
    // All store data cleared
  } catch (error) {
    console.error('Failed to clear store data', error);
  }
};

/**
 * Export store data for backup/debugging
 */
export const exportStoreData = (): Record<string, any> => {
  const data: Record<string, any> = {};
  
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      const item = defaultStorage.getItem(key);
      if (item) {
        data[key] = JSON.parse(item);
      }
    });
  } catch (error) {
    console.error('Failed to export store data', error);
  }
  
  return data;
};

/**
 * Import store data from backup
 */
export const importStoreData = (data: Record<string, any>): void => {
  try {
    Object.entries(data).forEach(([key, value]) => {
      if (Object.values(STORAGE_KEYS).includes(key as any)) {
        defaultStorage.setItem(key, JSON.stringify(value));
      }
    });
    
    // Store data imported successfully
  } catch (error) {
    console.error('Failed to import store data', error);
  }
};